import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Col, FormGroup, Input, Label, ListGroup, ListGroupItem, Modal, ModalBody, Row } from 'reactstrap';
import { Utils } from '../../services';
import { suggestDisambiguatedPricingId } from '../../services/pricingStockImpact';
import { pickRandomWizardIllustration } from '../../utils/wizardIllustrations';
import WizardModalFooter from '../WizardModalFooter';
import WizardModalHeader from '../WizardModalHeader';
import WizardStepShell from '../WizardStepShell';

import './PricingStockImpactModal.scss';

/**
 * Impact assistant shown when a stock override is made independent of the
 * ticket type: the same pricing id may be shared with other ticket types
 * and/or pricing lists, so the user is offered a way to resolve the ambiguity.
 */
const PricingStockImpactModal = ({
    isOpen,
    pricingId,
    matches,
    currentMaxQuantity,
    isOneTimePass,
    takenPricingIds,
    isAligning,
    onCancel,
    onAlign,
    onRename,
}) => {
    const { t, i18n } = useTranslation();
    const [newId, setNewId] = useState('');
    const [isRenaming, setIsRenaming] = useState(false);
    const [wizardIllustration, setWizardIllustration] = useState(null);

    useEffect(() => {
        if (isOpen) {
            setNewId(suggestDisambiguatedPricingId(pricingId, takenPricingIds || []));
            setWizardIllustration(pickRandomWizardIllustration());
            return;
        }
        setIsRenaming(false);
    }, [isOpen, pricingId, takenPricingIds]);

    const tickettypeMatches = matches?.tickettypes || [];
    const pricinglistMatches = matches?.pricinglists || [];

    return (
        <Modal
            isOpen={isOpen}
            toggle={onCancel}
            size="lg"
            modalClassName="PricingStockImpactModal"
            unmountOnClose
        >
            <WizardModalHeader
                toggle={onCancel}
                title={(
                    <>
                        <i className="fa fa-magic"></i>&nbsp;{t('tickettypes.stocks.impact_modal.title')}
                    </>
                )}
            />
            <ModalBody>
                <WizardStepShell
                    speechText={t('tickettypes.stocks.impact_modal.intro', { id: pricingId })}
                    speechAnimationKey={`pricing-stock-impact-${pricingId}`}
                    illustrationSrc={wizardIllustration}
                >
                    {tickettypeMatches.length > 0 && (
                        <>
                            <h6>{t('tickettypes.stocks.impact_modal.tickettypes_title')}</h6>
                            <ListGroup className="mb-3">
                                {tickettypeMatches.map(tt => (
                                    <ListGroupItem key={tt._id} className="d-flex justify-content-between align-items-center">
                                        <span>{Utils.localized_or_fallback(tt.name, i18n.language, tt._id)}</span>
                                        <span className="text-muted small">
                                            {tt.max_quantity != null
                                                ? t('tickettypes.stocks.impact_modal.current_max', { max: tt.max_quantity })
                                                : t('pricinglists.pricing.stock_unlimited')}
                                        </span>
                                    </ListGroupItem>
                                ))}
                            </ListGroup>
                        </>
                    )}
                    {pricinglistMatches.length > 0 && (
                        <>
                            <h6>{t('tickettypes.stocks.impact_modal.pricinglists_title')}</h6>
                            <ListGroup className="mb-3">
                                {pricinglistMatches.map(pl => (
                                    <ListGroupItem key={pl._id}>
                                        {Utils.localized_or_fallback(pl.name, i18n.language, pl._id)}
                                    </ListGroupItem>
                                ))}
                            </ListGroup>
                        </>
                    )}
                    <Alert color="info" className="mb-0 alert-inline">
                        <span className="alert-inline-body">
                            <i className="fa fa-info-circle"></i>&nbsp;
                            {t('tickettypes.stocks.impact_modal.explanation')}
                        </span>
                    </Alert>
                    {isRenaming && (
                        <FormGroup row className="mt-3 mb-0">
                            <Label sm={4}>{t('tickettypes.stocks.impact_modal.new_id_label')}</Label>
                            <Col sm={8}>
                                <Row>
                                    <Col xs={8}>
                                        <Input
                                            type="text"
                                            value={newId}
                                            onChange={e => setNewId(e.target.value.replace(/[^a-zA-Z0-9_]/, ''))}
                                        />
                                    </Col>
                                    <Col xs={4}>
                                        <Button
                                            color="success"
                                            block
                                            disabled={!newId || (takenPricingIds || []).includes(newId)}
                                            onClick={() => onRename(newId)}
                                        >
                                            <i className="fa fa-check"></i>&nbsp;{t('common.save')}
                                        </Button>
                                    </Col>
                                </Row>
                            </Col>
                        </FormGroup>
                    )}
                </WizardStepShell>
            </ModalBody>
            <WizardModalFooter onCancel={onCancel}>
                {!isOneTimePass && !isRenaming && (
                    <Button color="warning" className="mr-2" onClick={() => setIsRenaming(true)}>
                        <i className="fa fa-pencil"></i>&nbsp;{t('tickettypes.stocks.impact_modal.action_rename')}
                    </Button>
                )}
                <Button color="primary" disabled={isAligning || tickettypeMatches.length === 0} onClick={onAlign}>
                    {isAligning
                        ? <i className="fa fa-spinner fa-spin"></i>
                        : <i className="fa fa-arrows-h"></i>}
                    &nbsp;{t('tickettypes.stocks.impact_modal.action_align', { max: currentMaxQuantity ?? t('pricinglists.pricing.stock_unlimited') })}
                </Button>
            </WizardModalFooter>
        </Modal>
    );
};

export default PricingStockImpactModal;
